import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { steganographyAPI, AnalyzeResponse } from '../services/api';
import ImageComparison from './ImageComparison';
import MetricsPanel from './MetricsPanel';

interface AnalyzeTabProps {
  onImageProcessed?: () => void;
}

// Styled components for NEXUS design
const AnalyzeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '30px',
}));

const SectionPanel = styled(Box)(({ theme }) => ({
  border: '1px solid #00F0FF',
  backgroundColor: 'rgba(11, 8, 19, 0.6)',
  padding: '25px',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '15px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '20px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    marginBottom: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    marginBottom: '10px',
  },
}));

const DropzoneArea = styled(Box)<{ isdragactive: boolean }>(({ isdragactive, theme }) => ({
  border: isdragactive ? '2px dashed #00F0FF' : '2px dashed #6B6A78',
  borderRadius: '5px',
  padding: '40px',
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: isdragactive ? 'rgba(0, 240, 255, 0.1)' : 'transparent',
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    padding: '30px',
    minHeight: '150px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
    minHeight: '120px',
  },
  '&:hover': {
    borderColor: '#00F0FF',
    boxShadow: '0 0 20px rgba(0, 240, 255, 0.2)',
  },
}));

const DropzoneIcon = styled(Box)(({ theme }) => ({
  fontSize: '48px',
  color: '#6B6A78',
  marginBottom: '15px',
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
    marginBottom: '10px',
  },
}));

const DropzoneText = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const DropzoneSubtext = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '8px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '9px',
  },
}));

const NexusButton = styled(Button)<{ nexusVariant?: 'primary' | 'secondary' | 'danger' | string }>(({ nexusVariant = 'primary', theme }) => ({
  border: nexusVariant === 'primary' ? '2px solid #00F0FF' : 
           nexusVariant === 'secondary' ? '2px solid #FFD200' : 
           '2px solid #FF007A',
  color: nexusVariant === 'primary' ? '#00F0FF' : 
         nexusVariant === 'secondary' ? '#FFD200' : 
         '#FF007A',
  padding: '12px 25px',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    padding: '10px 20px',
    fontSize: '10px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 15px',
    fontSize: '9px',
  },
  '&:hover': {
    borderColor: nexusVariant === 'primary' ? '#00F0FF' : 
             nexusVariant === 'secondary' ? '#FFD200' : 
             '#FF007A',
    color: nexusVariant === 'primary' ? '#00F0FF' : 
          nexusVariant === 'secondary' ? '#FFD200' : 
          '#FF007A',
    boxShadow: `0 0 15px ${nexusVariant === 'primary' ? 'rgba(0, 240, 255, 0.5)' : 
                           nexusVariant === 'secondary' ? 'rgba(255, 210, 0, 0.5)' : 
                           'rgba(255, 0, 122, 0.5)'}`,
    transform: 'translateY(-2px)',
  },
  '&:active': {
    backgroundColor: nexusVariant === 'primary' ? '#00F0FF' : 
                   nexusVariant === 'secondary' ? '#FFD200' : 
                   '#FF007A',
    color: '#0B0813',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const StatusMessage = styled(Box)<{ type: 'error' | 'success' }>(({ type, theme }) => ({
  padding: '15px',
  border: `1px solid ${type === 'error' ? '#FF007A' : '#39FF14'}`,
  backgroundColor: type === 'error' ? 'rgba(255, 0, 122, 0.1)' : 'rgba(57, 255, 20, 0.1)',
  borderRadius: '5px',
  color: type === 'error' ? '#FF007A' : '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '13px',
  marginBottom: '20px',
  boxShadow: type === 'error' ? '0 0 15px rgba(255, 0, 122, 0.2)' : '0 0 15px rgba(57, 255, 20, 0.2)',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    padding: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '10px',
  },
}));

const ImagePreview = styled(Box)(({ theme }) => ({
  marginTop: '20px',
  padding: '15px',
  border: '1px solid #39FF14',
  backgroundColor: 'rgba(57, 255, 20, 0.05)',
  borderRadius: '5px',
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
    marginTop: '15px',
  },
}));

const PreviewImage = styled('img')(({ theme }) => ({
  width: '100%',
  maxHeight: '200px',
  objectFit: 'contain',
  border: '1px solid #39FF14',
  [theme.breakpoints.down('sm')]: {
    maxHeight: '150px',
  },
}));

const FileInfo = styled(Typography)(({ theme }) => ({
  color: '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '10px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const HelperText = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '5px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const AnalysisSummary = styled(Box)(({ theme }) => ({
  border: '1px solid #39FF14',
  backgroundColor: 'rgba(57, 255, 20, 0.05)',
  padding: '20px',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(57, 255, 20, 0.2)',
}));

const SummaryItem = styled(Box)(({ theme }) => ({
  marginBottom: '15px',
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  textTransform: 'uppercase',
  marginBottom: '5px',
}));

const SummaryValue = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  fontWeight: 'bold',
}));

const SummaryDivider = styled(Divider)(({ theme }) => ({
  borderColor: '#6B6A78',
  opacity: 0.3,
}));

interface ImageFile {
  file: File;
  url: string;
  type: 'original' | 'encoded';
}

const AnalyzeTab: React.FC<AnalyzeTabProps> = ({ onImageProcessed }) => {
  const [originalImage, setOriginalImage] = useState<ImageFile | null>(null);
  const [encodedImage, setEncodedImage] = useState<ImageFile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [metrics, setMetrics] = useState<any>(null);

  const { getRootProps: getOriginalRootProps, getInputProps: getOriginalInputProps, isDragActive: isOriginalDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.bmp', '.jpg', '.jpeg']
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const url = URL.createObjectURL(file);
        setOriginalImage({ file, url, type: 'original' });
        setError('');
        setMetrics(null);
      }
    },
    onError: (error) => {
      setError('File upload error: ' + error.message);
    }
  });

  const { getRootProps: getEncodedRootProps, getInputProps: getEncodedInputProps, isDragActive: isEncodedDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.bmp', '.jpg', '.jpeg']
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const url = URL.createObjectURL(file);
        setEncodedImage({ file, url, type: 'encoded' });
        setError('');
        setMetrics(null);
      }
    },
    onError: (error) => {
      setError('File upload error: ' + error.message);
    }
  });

  const handleAnalyze = async () => {
    // Image validation
    if (!originalImage || !encodedImage) {
      setError('Please upload both original and encoded images');
      return;
    }
    
    // Validate image types
    const validImageTypes = ['image/png', 'image/bmp', 'image/jpeg', 'image/jpg'];
    
    if (!validImageTypes.includes(originalImage.file.type)) {
      setError('Invalid original image type. Please use PNG, BMP, or JPEG.');
      return;
    }
    
    if (!validImageTypes.includes(encodedImage.file.type)) {
      setError('Invalid encoded image type. Please use PNG, BMP, or JPEG.');
      return;
    }
    
    // Validate image sizes (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    
    if (originalImage.file.size > maxSize) {
      setError('Original image size exceeds 50MB limit.');
      return;
    }
    
    if (encodedImage.file.size > maxSize) {
      setError('Encoded image size exceeds 50MB limit.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response: AnalyzeResponse = await steganographyAPI.analyze(
        originalImage.file,
        encodedImage.file
      );

      if (response.success && response.metrics) {
        setMetrics(response.metrics);
        onImageProcessed?.();
      } else {
        setError(response.error || 'Analysis failed');
      }
    } catch (err: any) {
      setError(err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSwap = () => {
    if (originalImage && encodedImage) {
      const temp = originalImage;
      setOriginalImage({ ...encodedImage, type: 'original' });
      setEncodedImage({ ...temp, type: 'encoded' });
      setMetrics(null);
    }
  };

  
  const handleClear = () => {
    setOriginalImage(null);
    setEncodedImage(null);
    setMetrics(null);
    setError('');
  };

  return (
    <AnalyzeContainer>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Image Upload Areas */}
        <Grid item xs={12} md={6}>
          <SectionPanel>
            <SectionTitle>01 / ORIGINAL WORLD</SectionTitle>
            
            <DropzoneArea {...getOriginalRootProps()} isdragactive={isOriginalDragActive}>
              <input {...getOriginalInputProps()} />
              <DropzoneIcon>📁</DropzoneIcon>
              <DropzoneText>
                {isOriginalDragActive ? 'DROP IMAGE HERE' : 'DRAG & DROP IMAGE OR CLICK TO SELECT'}
              </DropzoneText>
              <DropzoneSubtext>
                PNG, BMP, JPEG (MAX 50MB)
              </DropzoneSubtext>
            </DropzoneArea>

            {originalImage && (
              <ImagePreview>
                <PreviewImage
                  src={originalImage.url}
                  alt="Original"
                />
                <FileInfo>
                  // FILE: {originalImage.file.name} ({(originalImage.file.size / 1024).toFixed(1)} KB)
                </FileInfo>
              </ImagePreview>
            )}
          </SectionPanel>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionPanel>
            <SectionTitle>02 / ENCODED WORLD</SectionTitle>
            
            <DropzoneArea {...getEncodedRootProps()} isdragactive={isEncodedDragActive}>
              <input {...getEncodedInputProps()} />
              <DropzoneIcon>📁</DropzoneIcon>
              <DropzoneText>
                {isEncodedDragActive ? 'DROP IMAGE HERE' : 'DRAG & DROP IMAGE OR CLICK TO SELECT'}
              </DropzoneText>
              <DropzoneSubtext>
                PNG, BMP, JPEG (MAX 50MB)
              </DropzoneSubtext>
            </DropzoneArea>

            {encodedImage && (
              <ImagePreview>
                <PreviewImage
                  src={encodedImage.url}
                  alt="Encoded"
                />
                <FileInfo>
                  // FILE: {encodedImage.file.name} ({(encodedImage.file.size / 1024).toFixed(1)} KB)
                </FileInfo>
              </ImagePreview>
            )}
          </SectionPanel>
        </Grid>

        {/* Controls */}
        <Grid item xs={12}>
          <SectionPanel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
              <NexusButton
                nexusVariant="primary"
                onClick={handleAnalyze}
                disabled={loading || !originalImage || !encodedImage}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : '📊 ANALYZE IMAGES'}
              </NexusButton>
              
              <NexusButton
                nexusVariant="secondary"
                onClick={handleSwap}
                disabled={!originalImage || !encodedImage}
              >
                🔄 SWAP IMAGES
              </NexusButton>
              
              <NexusButton
                nexusVariant="danger"
                onClick={handleClear}
              >
                CLEAR ALL
              </NexusButton>
            </Box>
          </SectionPanel>
        </Grid>

        {/* Error Display */}
        {error && (
          <Grid item xs={12}>
            <StatusMessage type="error">
              // ERROR: {error}
            </StatusMessage>
          </Grid>
        )}

        {/* Analysis Results */}
        {metrics && (
          <>
            <Grid item xs={12} md={6}>
              <MetricsPanel metrics={metrics} />
            </Grid>

            <Grid item xs={12} md={6}>
              <AnalysisSummary>
                <SectionTitle>ANALYSIS SUMMARY</SectionTitle>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <SummaryItem>
                    <SummaryLabel>QUALITY ASSESSMENT</SummaryLabel>
                    <SummaryValue>{metrics.quality_rating}</SummaryValue>
                  </SummaryItem>

                  <SummaryDivider />

                  <SummaryItem>
                    <SummaryLabel>IMAGE SIMILARITY</SummaryLabel>
                    <SummaryValue>
                      PSNR: {metrics.psnr === Infinity ? 'PERFECT' : `${metrics.psnr.toFixed(2)} DB`}
                    </SummaryValue>
                    {metrics.ssim !== undefined && (
                      <SummaryValue>
                        SSIM: {metrics.ssim.toFixed(4)}
                      </SummaryValue>
                    )}
                  </SummaryItem>

                  <SummaryDivider />

                  <SummaryItem>
                    <SummaryLabel>FILE SIZE IMPACT</SummaryLabel>
                    <SummaryValue>
                      ORIGINAL: {(metrics.file_size_impact.original_size / 1024).toFixed(1)} KB
                    </SummaryValue>
                    <SummaryValue>
                      ENCODED: {(metrics.file_size_impact.encoded_size / 1024).toFixed(1)} KB
                    </SummaryValue>
                    <SummaryValue sx={{ color: '#39FF14' }}>
                      INCREASE: +{metrics.file_size_impact.percentage_increase.toFixed(2)}%
                    </SummaryValue>
                  </SummaryItem>

                  {metrics.histogram_correlation && (
                    <>
                      <SummaryDivider />
                      <SummaryItem>
                        <SummaryLabel>HISTOGRAM CORRELATION</SummaryLabel>
                        {Object.entries(metrics.histogram_correlation).map(([channel, correlation]: [string, any]) => (
                          <SummaryValue key={channel}>
                            {channel.charAt(0).toUpperCase() + channel.slice(1)}: {correlation.toFixed(4)}
                          </SummaryValue>
                        ))}
                      </SummaryItem>
                    </>
                  )}
                </Box>
              </AnalysisSummary>
            </Grid>

            <Grid item xs={12}>
              <ImageComparison
                originalImage={originalImage?.url || ''}
                encodedImage={encodedImage?.url || ''}
              />
            </Grid>
          </>
        )}
      </Grid>
    </AnalyzeContainer>
  );
};

export default AnalyzeTab;
